"use client";

import { JSX, useMemo } from "react";
import {
  type LucideIcon,
  GitCommit,
  Pencil,
  Trash2,
  CheckCircle,
  MessageCircle,
  UserPlus,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { CascadeIn } from "@/components/motion/cascade-in";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import type { ModuleActivityFeedData } from "@/types/modules";
import { renderInlineMarkdown, stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import {
  buildResolvedLegendItems,
  legendIndexOfCategory,
  resolveCategories,
  resolveCategoryColor,
} from "./shared/category-resolve";
import { type ListVariant } from "./shared/lists";

const ICON_MAP: Record<string, LucideIcon> = {
  "git-commit": GitCommit,
  pencil: Pencil,
  "trash-2": Trash2,
  "check-circle": CheckCircle,
  "message-circle": MessageCircle,
  "user-plus": UserPlus,
  plus: Plus,
};

type ResolvedItem = {
  id: string | number;
  icon: LucideIcon;
  iconName: string;
  tint: string;
  actor: string;
  verb: string;
  target: string;
  detail?: string;
  time: string;
  categoryId?: string;
};

export default function ModuleActivityFeed({
  data,
  variant = "icon",
  grayscale,
  accent,
  className,
}: {
  data: ModuleActivityFeedData;
  variant?: ListVariant;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const items = useMemo<ResolvedItem[]>(
    () =>
      data.items.map((it) => ({
        ...it,
        icon: ICON_MAP[it.icon] ?? GitCommit,
        iconName: it.icon in ICON_MAP ? it.icon : "git-commit",
      })),
    [data.items],
  );

  const selection = useSelectionGroup();
  const row = useHoverSelect<string | number>({ group: selection });
  const effective = row.effective;
  const hasHover = useHoverCapable();

  const resolved = useMemo(
    () => resolveCategories(data.categories, []),
    [data.categories],
  );
  const free = resolved.free;
  const categoryLegend = useLegendHoverSelect({ group: selection });
  const categoryLegendItems = useMemo(
    () => (free ? buildResolvedLegendItems(items, resolved) : []),
    [free, items, resolved],
  );
  const catFilter =
    categoryLegend.filter !== null
      ? categoryLegendItems[categoryLegend.filter]?.id ?? null
      : null;

  const legendSelectedIndex =
    categoryLegend.selected ??
    legendIndexOfCategory(
      categoryLegendItems,
      items.find((it) => it.id === row.selected)?.categoryId,
    );

  return (
    <div
      className={cn("w-full", free && "flex flex-col gap-3", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <SwipeScroll className="pb-2 [mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)]">
        <CascadeIn
          keySignature="activity-feed"
          className="grid grid-rows-3 grid-flow-col auto-cols-[minmax(240px,auto)] gap-y-[2px] gap-x-5 w-max pr-10"
        >
          {items.map((item, i) => {
            const IconCmp = item.icon;
            const isHovered = effective === item.id;
            const catDim =
              free && catFilter !== null && item.categoryId !== catFilter;
            const dim = (effective !== null && !isHovered) || catDim;
            const tint =
              grayscale && !item.tint.startsWith("var(--chart-")
                ? "var(--module-accent)"
                : item.tint;
            const catColor = free
              ? resolveCategoryColor(item.categoryId, resolved.categories, tint)
              : null;
            const markerTone =
              catColor ?? (variant === "icon" ? tint : "var(--muted)");
            const markerRing =
              catColor ??
              (variant === "icon" ? tint : "var(--border-strong)");

            return (
              <button
                key={item.id}
                type="button"
                aria-pressed={row.selected === item.id}
                className="flex w-full items-start gap-2 py-1.5 px-2 rounded-md text-left cursor-pointer"
                style={{
                  opacity: dim ? 0.4 : 1,
                  transition: "opacity 0.15s, background 0.15s",
                }}
                onMouseEnter={hasHover ? () => row.setHovered(item.id) : undefined}
                onMouseLeave={hasHover ? () => row.setHovered(null) : undefined}
                onFocus={hasHover ? () => row.setHovered(item.id) : undefined}
                onBlur={hasHover ? () => row.setHovered(null) : undefined}
                onClick={(e) =>
                  row.select(item.id, {
                    kind: "row",
                    id: item.id,
                    index: i,
                    element: e.currentTarget,
                  })
                }
              >
                <div
                  className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                  style={{
                    background: "var(--bg)",
                    boxShadow: isHovered
                      ? `inset 0 0 0 1px ${markerRing}`
                      : "inset 0 0 0 1px var(--border)",
                    color: markerTone,
                    transition: "box-shadow 0.15s, color 0.15s",
                  }}
                >
                  {variant === "icon" ? (
                    <AnimatedIcon name={item.iconName} fallback={IconCmp} size={13} strokeWidth={2} />
                  ) : (
                    <span className="text-[10px] tabular-nums font-medium">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className={`text-xs leading-snug ${MODULE_LABEL_WRAP.row}`}>
                    <span className="text-[var(--fg)] font-medium">{stripInlineMarkdown(item.actor)}</span>
                    <span className="text-[var(--muted)]"> {stripInlineMarkdown(item.verb)} </span>
                    <span className="text-[var(--fg)] [overflow-wrap:anywhere]">{stripInlineMarkdown(item.target)}</span>
                  </div>
                  {item.detail && (
                    <div className="text-[11px] text-[var(--subtle)] mt-0.5 truncate leading-snug">
                      {renderInlineMarkdown(item.detail)}
                    </div>
                  )}
                </div>

                <div className="shrink-0 pt-0.5">
                  <span className="text-[10px] text-[var(--subtle)] tabular-nums">
                    {item.time}
                  </span>
                </div>
              </button>
            );
          })}
        </CascadeIn>
      </SwipeScroll>

      {free && (
        <SwipeScroll>
          <Legend
            items={categoryLegendItems}
            hoveredIndex={categoryLegend.hovered}
            onHoverChange={categoryLegend.setHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={categoryLegend.select}
            onLabelRef={categoryLegend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
